import { IntersectionOptions, useInView } from 'react-intersection-observer';
import React, { CSSProperties, FC } from 'react';
import clsx from 'clsx';
import useMediaQuery from '@material-ui/core/useMediaQuery/useMediaQuery';
import useTheme from '@material-ui/core/styles/useTheme';

interface LazyBackgroundImageProps
  extends React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement> {
  /**
   * optional component string, defaults to div
   */
  component?: React.ElementType;
  /**
   * url of the background image
   */
  url: string;
  /**
   * css class for the containing div
   */
  className?: string;
  /**
   * css class applied when the component is visible
   */
  inViewClassName?: string;
  /**
   * urlSm: the url to show at breakpoints.up('sm')
   */
  urlSm?: string;
  /**
   * urlSm: the url to show at breakpoints.up('md')
   */
  urlMd?: string;
  /**
   * urlSm: the url to show at breakpoints.up('lg')
   */
  urlLg?: string;
  /**
   * urlSm: the url to show at breakpoints.up('xl')
   */
  urlXl?: string;

  /**
   * options for react-intersection-observer - https://www.npmjs.com/package/react-intersection-observer
   * defaults to { triggerOnce: true, rootMargin: '200px 0px' }
   */
  intersectionOptions?: IntersectionOptions;

  backgroundPosition?: CSSProperties['backgroundPosition'];
  backgroundColor?: CSSProperties['backgroundColor'];
  backgroundRepeat?: CSSProperties['backgroundRepeat'];
  backgroundSize?: CSSProperties['backgroundSize'];
}

const defaultIntersectionOptions = {
  triggerOnce: true,
  rootMargin: '200px 0px',
};

/**
 * Lazily load a background image
 * There is a base className that should be used to set up the
 * layout of this component in terms of the space it will take up
 * it will set the background-image style inline when in view,
 * and optionally, set an additional classNAme when it becomes inView
 * This is useful as an example to provide the initial state, then transition things when it becomes inView
 */
const LazyBackgroundImage: FC<LazyBackgroundImageProps> = ({
  component = 'div',
  className,
  inViewClassName,
  url,
  urlSm,
  urlMd,
  urlLg,
  urlXl,
  children,
  backgroundColor,
  backgroundPosition,
  backgroundRepeat,
  backgroundSize,
  intersectionOptions = defaultIntersectionOptions,
  ...rest
}) => {
  const Component = component;
  const [ref, inView] = useInView(intersectionOptions);

  const theme = useTheme();
  const matchesSm = useMediaQuery(theme.breakpoints.up('sm'));
  const matchesMd = useMediaQuery(theme.breakpoints.up('md'));
  const matchesLg = useMediaQuery(theme.breakpoints.up('lg'));
  const matchesXl = useMediaQuery(theme.breakpoints.up('xl'));

  const imageUrls = {
    sm: urlSm,
    md: urlMd,
    lg: urlLg,
    xl: urlXl,
  };

  let imageSrc = url;
  //search for the right match
  if (matchesSm && imageUrls.sm) {
    imageSrc = imageUrls.sm;
  }
  if (matchesMd && imageUrls.md) {
    imageSrc = imageUrls.md;
  }
  if (matchesLg && imageUrls.lg) {
    imageSrc = imageUrls.lg;
  }
  if (matchesXl && imageUrls.xl) {
    imageSrc = imageUrls.xl;
  }

  return (
    <Component
      {...rest}
      ref={ref}
      className={clsx(className, inView ? inViewClassName : undefined)}
      style={{
        backgroundImage: inView ? `url(${imageSrc})` : '',
        ...{ backgroundColor, backgroundPosition, backgroundRepeat, backgroundSize },
      }}
    >
      {children}
    </Component>
  );
};

export default LazyBackgroundImage;
